// Static configuration for the Relationship drawer. Colours, labels and
// section order follow the Relationship Layer design mock.

export const RELATIONSHIP_DRAWER_WIDTH_CLS = "w-[1400px] max-w-[95vw]";

export const RELATIONSHIP_TABLE_PAGE_SIZE = 8;
export const RELATIONSHIP_SEARCH_MAX_LENGTH = 120;

/* Mock palette. Kept as hex because the graph, dots and type borders take
   raw colours; Tailwind classes cannot express the mock's exact tints. */
export const REL_COLOR = {
  accent: "#5b8aff",
  green: "#10b981",
  red: "#ef4444",
  amber: "#f59e0b",
  purple: "#8b5cf6",
  cyan: "#06b6d4",
  pink: "#ec4899",
  orange: "#f97316",
  teal: "#14b8a6",
  neutral: "#7a819a",
} as const;

/* Type colour map (mock `TC`). */
export const RELATIONSHIP_TYPE_HEX: Record<string, string> = {
  IMPLEMENTS: REL_COLOR.green,
  REFINES: REL_COLOR.accent,
  SUPPORTS: REL_COLOR.cyan,
  DEPENDS_ON: REL_COLOR.amber,
  PARTIALLY_IMPLEMENTS: REL_COLOR.purple,
  CONTRADICTS: REL_COLOR.red,
  DUPLICATES: REL_COLOR.pink,
  INTERNAL_DOCUMENT: REL_COLOR.cyan,
  EXTERNAL_STANDARD: REL_COLOR.accent,
  EXTERNAL_FRAMEWORK: REL_COLOR.purple,
  EXTERNAL_REGULATION: REL_COLOR.orange,
  SECTION: REL_COLOR.purple,
  REQUIREMENT: REL_COLOR.pink,
  APPENDIX: REL_COLOR.teal,
  BELONGS_TO: REL_COLOR.teal,
  REFERENCES: REL_COLOR.accent,
  OTHER: REL_COLOR.neutral,
};
export const RELATIONSHIP_TYPE_HEX_FALLBACK = REL_COLOR.neutral;

/* Type label map (mock `TL`). */
export const RELATIONSHIP_TYPE_LABEL: Record<string, string> = {
  IMPLEMENTS: "Implements",
  REFINES: "Refines",
  SUPPORTS: "Supports",
  DEPENDS_ON: "Depends On",
  PARTIALLY_IMPLEMENTS: "Partially Implements",
  CONTRADICTS: "Contradicts",
  DUPLICATES: "Duplicates",
  INTERNAL_DOCUMENT: "Internal Document",
  EXTERNAL_STANDARD: "External Standard",
  EXTERNAL_FRAMEWORK: "External Framework",
  EXTERNAL_REGULATION: "External Regulation",
  SECTION: "Section",
  REQUIREMENT: "Requirement",
  APPENDIX: "Appendix",
  BELONGS_TO: "Belongs To",
  REFERENCES: "References",
  OTHER: "Other",
};

/* Which findings land in which table. */
export const SEMANTIC_RELATIONSHIP_TYPES = new Set([
  "IMPLEMENTS",
  "PARTIALLY_IMPLEMENTS",
  "REFINES",
  "SUPPORTS",
  "DEPENDS_ON",
  "CONTRADICTS",
  "DUPLICATES",
]);
export const SYMMETRIC_RELATIONSHIP_TYPES = new Set(["CONTRADICTS", "DUPLICATES"]);
export const CROSS_DOCUMENT_TARGET_TYPES = new Set([
  "INTERNAL_DOCUMENT",
  "EXTERNAL_STANDARD",
  "EXTERNAL_FRAMEWORK",
  "EXTERNAL_REGULATION",
  "EXTERNAL_DOCUMENT",
  "DOCUMENT",
  "OTHER",
]);
export const CROSS_DOCUMENT_RELATIONSHIP_TYPES = new Set([
  "CITES",
  "CROSS_DOCUMENT_REFERENCE",
  "REFERENCES_DOCUMENT",
]);
export const EXTERNAL_DOC_TYPES = new Set([
  "EXTERNAL_STANDARD",
  "EXTERNAL_FRAMEWORK",
  "EXTERNAL_REGULATION",
]);

export const STATUS_RESOLVED = "RESOLVED";
export const STATUS_UNRESOLVED = "UNRESOLVED";
export const STATUS_REVIEW = "REVIEW_REQUIRED";
export const UNNAMED_TARGET = "(unnamed)";

/* Filter sentinel + shared item labels — used everywhere pager/filter state
   defaults to "all" or a plural noun so the strings stop leaking into JSX. */
export const FILTER_ALL = "all";

export const TABLE_ITEM_LABELS = {
  citations: "citations",
  references: "references",
  relationships: "relationships",
} as const;

/* Layer status label lookup (mock's Partial / Complete strings kept out
   of JSX). Reads take an already-title-cased status from the API. */
export const RELATIONSHIP_LAYER_STATUS_LABEL = {
  partial: "Partial",
  complete: "Complete",
  clean: "Clean",
} as const;

export const RELATIONSHIP_DOC_FALLBACK_LABEL = "Document";

/* Confidence colour thresholds (percent), fed by `confidenceHex`. */
export const CONFIDENCE_STRONG_PCT = 80;
export const CONFIDENCE_MODERATE_PCT = 60;

/* Cited-document status chip labels. */
export const CITED_STATUS_LABEL = {
  resolved: "Resolved",
  unresolved: "Unresolved",
} as const;

/* Tag tones consumed by RelationshipHeader — swaps a nested ternary for a
   lookup keyed by the tone name. */
export type RelTagTone = "accent" | "green" | "amber";
export const REL_TAG_TONE_HEX: Record<RelTagTone, string> = {
  accent: REL_COLOR.accent,
  green: REL_COLOR.green,
  amber: REL_COLOR.amber,
};

/* Banner tones — icon key + hex, so `RelBanner` becomes table-driven. */
export type RelBannerTone = "red" | "amber" | "green";
export const REL_BANNER_TONE_HEX: Record<RelBannerTone, string> = {
  red: REL_COLOR.red,
  amber: REL_COLOR.amber,
  green: REL_COLOR.green,
};

/* Status tag meta — keyed on status enum, defines icon + tone.  Anything
   not in the map falls back to the generic red-tone chip. */
export type StatusTagKind = "resolved" | "review" | "other";
export interface StatusTagMeta {
  kind: StatusTagKind;
  label: string;
  hex: string;
}
export const RELATIONSHIP_STATUS_TAG_META: Record<string, StatusTagMeta> = {
  [STATUS_RESOLVED]: {
    kind: "resolved",
    label: CITED_STATUS_LABEL.resolved,
    hex: REL_COLOR.green,
  },
  [STATUS_REVIEW]: { kind: "review", label: "Review", hex: REL_COLOR.amber },
};

/* Graph geometry / weight tuning kept out of the render module. */
export const GRAPH_HUB_CENTER_Y = 250;
export const GRAPH_EDGE_WEIGHT = {
  base: 1.5,
  perCitation: 0.5,
  cap: 6,
} as const;

/* Cited-document icon lookup — pass in the type key, return the lucide
   icon name. The section file swaps the icon component from this key. */
export type CitedDocIconKey =
  | "landmark"
  | "network"
  | "scale"
  | "building"
  | "fallback";
export const CITED_DOC_ICON_KEY: Record<string, CitedDocIconKey> = {
  EXTERNAL_STANDARD: "landmark",
  EXTERNAL_FRAMEWORK: "network",
  EXTERNAL_REGULATION: "scale",
  INTERNAL_DOCUMENT: "building",
};

/* Accent colour surfaced as a Tailwind arbitrary-value string so the four
   filter / focus rings can all reference the same literal.  Tailwind can't
   interpolate at runtime, so we keep a `.replace()`-friendly base string. */
export const REL_ACCENT_HEX = REL_COLOR.accent;
export const REL_FILTER_ACTIVE_CLS =
  "border-[#5b8aff] bg-[#5b8aff]/[0.12] text-[#5b8aff]";
export const REL_FOCUS_RING_CLS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5b8aff]/50";
export const REL_INPUT_FOCUS_CLS = "focus-visible:border-[#5b8aff]";

/* Graph geometry (SVG user units). Radial orbit layout: the document sits in a
   card at the centre, cited documents ride two elliptical arcs either side. */
export const GRAPH_VIEWBOX = { width: 1000, height: 520 } as const;
export const GRAPH_ORBIT = { rx: 292, ry: 178 } as const;
export const GRAPH_HUB = { width: 212, height: 96 } as const;
export const GRAPH_ARC_SPREAD = 58;
export const GRAPH_MAX_NODES_PER_SIDE = 7;

/* Right-column colours carry meaning: amber = regulator-mandated, violet =
   standard, teal = framework. Left-column colours come from availability. */
export const GRAPH_EXTERNAL_NODE: Record<string, { color: string; label: string }> = {
  EXTERNAL_REGULATION: { color: REL_COLOR.amber, label: "Regulation" },
  EXTERNAL_STANDARD: { color: REL_COLOR.purple, label: "Standard" },
  EXTERNAL_FRAMEWORK: { color: REL_COLOR.teal, label: "Framework" },
};
export const GRAPH_EXTERNAL_FALLBACK = { color: REL_COLOR.accent, label: "External" };

export const RELATIONSHIP_COPY = {
  title: "Relationship Intelligence",
  description:
    "This layer shows how the document points at itself and at other documents internal section references and role assignments, and citations of other policies, standards and regulations.",
  sheetDescription:
    "Cross-document citations, within-document references and semantic relationships between requirements.",
  loadingLabel: "Loading relationships...",
  layerTag: "Layer · Relationship",
  cleanTag: "clean",
  viewSourceLabel: "View source evidence",
  searchEvidence: "Search evidence...",
  searchRelationships: "Search relationships...",
  noRows: "No rows match these filters.",
  noCitedDocs: "This document does not cite any other documents.",
  noCitations: "No cross-document citations were detected.",
  noInternalRefs: "No within-document references were detected.",
  noSemantic: "No semantic relationships between requirements were detected.",
  graphEmpty: "No document-to-document connections to draw for this document.",
  graphFooter: "Line weight shows how many citations point at each document",
  graphOrgZone: "Organisation library",
  graphExtZone: "External obligations",
  graphHubCaption: "This document",
  graphInLibrary: "Available in content registry",
  graphMissing: "Not uploaded yet",
  graphMoreOrg: "more organisation documents cited",
  graphMoreExt: "more external documents cited",
  mutual: "mutual",
} as const;

export const RELATIONSHIP_SECTIONS = {
  summary: { title: "Layer Summary", dot: REL_COLOR.green },
  cited: { title: "Cited Documents", dot: REL_COLOR.cyan },
  cross: { title: "Across-Document Citations", dot: REL_COLOR.cyan },
  internal: { title: "Within-Document References", dot: REL_COLOR.purple },
  semantic: { title: "Semantic Relationships", dot: REL_COLOR.pink },
  graph: { title: "Relationship Graph", dot: REL_COLOR.accent, right: "Document-to-document connections" },
} as const;

export const GRAPH_LEGEND_GROUPS = [
  {
    title: "Organisation library",
    items: [
      { label: "Available", color: REL_COLOR.green },
      { label: "Referenced, not uploaded", color: REL_COLOR.red },
    ],
  },
  {
    title: "External obligations",
    items: [
      { label: "Regulation", color: REL_COLOR.amber },
      { label: "Standard", color: REL_COLOR.purple },
      { label: "Framework", color: REL_COLOR.teal },
    ],
  },
] as const;
